import * as cheerio from 'cheerio';
import cheerioTableparser from 'cheerio-tableparser';
import axios from 'axios';
import { Base64 } from 'js-base64';

import { extractMetaDataFromCDA } from './metaData';
import createSearchData from './createSearchData';

import CDAStylesheet from './cda.xsl';
import fileDownload from 'js-file-download';

function loadXMLDoc(filename) {
  let xhttp;

  if (window.ActiveXObject) {
    xhttp = new window.ActiveXObject('Msxml2.XMLHTTP');
  } else {
    xhttp = new XMLHttpRequest();
  }
  xhttp.open('GET', filename, false);
  try {
    xhttp.responseType = 'msxml-document';
  } catch (err) {} // Helping IE11
  xhttp.send('');
  return xhttp.responseXML;
}

function getNodeText($, node) {
  const parts = $(node).contents().map((_, n) => {
    if (n.type === 'text') return n.data.trim();
    
    if (n.type === 'tag') {
      if (n.name === 'br') return '\n';
      let text = getNodeText($, n);
      if (n.name === 'table' && text) text = `\n${text.trim()}\n`;

      return text;
    }

    return '';
  }).get();

  return parts.filter(Boolean).join(' ');
}


function formatXMLDoc(xmlDoc) {
  let xml = loadXMLDoc(xmlDoc);
  let xsl = loadXMLDoc(CDAStylesheet);

  let xsltProcessor = new XSLTProcessor();
  xsltProcessor.importStylesheet(xsl);
  let resultDocument = xsltProcessor.transformToFragment(xml, document);

  return resultDocument;
}

const flattenNestedTables = ($, table) => {
  $(table)
    .find('td')
    .each((_, td) => {
      const nestedTables = $(td).find('table');
      if (nestedTables.length) $(td).html(getNodeText($, td));
    });

  return table;
};

const parseDocument = async (document) => {
  if (!document) {
    return;
  }

  let xmlDocumentStr;
  const getPlainContent = ($, str) => {
    if (!str) return '';

    if (/[<&]/gi.test(str)) {
      try {
        return getNodeText($, `<div>${str}</div>`);
      } catch (e) {
      }
    }

    return str.trim();
  }

  if (document.startsWith('/static')) {
    try {
      const res = await axios.get(document, {
        'Content-Type': 'application/xml'
      });

      xmlDocumentStr = res.data;
    } catch (error) {
      console.dir(error);
      return;
    }
  } else {
    xmlDocumentStr = document;
  }

  const b64 = Base64.encode(xmlDocumentStr);

  const dataUrl = `data:text/xml;base64,${b64}`;

  const transformed = formatXMLDoc(dataUrl);

  const transformedHtmlStr = new XMLSerializer().serializeToString(transformed);

  // const blob = new Blob([transformedHtmlStr], { type: 'text/html' });
  // fileDownload(blob, 'result.html');

  const $ = cheerio.load(transformedHtmlStr);

  // Cheerio Table parser extension
  cheerioTableparser($);

  const metaData = extractMetaDataFromCDA(xmlDocumentStr);

  /**
   * Document Body
   */
  let body = [];

  const sections = $('div[class="container-fluid header"]').toArray();

  sections.forEach((_section) => {
    let section = { title: '', data: [] };

    const sectionTitle = $(_section).find('h1[class="section-title"]').text();
    section.title = sectionTitle;

    const sectionBody = $(_section).find('div[class="section-text"]');

    const sectionContents = $(sectionBody).contents().toArray();

    const parseTable = (node) => {
      const table = flattenNestedTables($, node);
      const tableHead = $(table).find('> thead');
      if (!tableHead.length) {
        const tableContent = getNodeText($, table);

        const paragraphs = tableContent.split(/\n{2,}/).filter((p) => p.trim());

        paragraphs.forEach((paragraph) => {
          section.data.push({ type: 'paragraph', content: paragraph.trim() });
        });
      } else {
        let columns, data;

        const parsedTableData = $(table).parsetable(false, false, false);

        columns = parsedTableData.map((node) => node[0]).filter((col) => !!col).map(col => getPlainContent($, col));

        data = [];

        const tableDataWithoutColumnName = parsedTableData.map((arr) =>
          arr.slice(1)
        );

        let idx = 0;
        let max = tableDataWithoutColumnName[0].length;

        while (idx < max) {
          let row = {};
          columns.forEach((col) => {
            let colIdx = columns.indexOf(col);
            row[col] = tableDataWithoutColumnName[colIdx][idx];
          });

          data.push(row);

          idx++;
        }

        const formattedData = data.map((row) => {
          let newRow = {};

          Object.entries(row).forEach(([key, value]) => {
            newRow[key] = getPlainContent($, value);
          });

          return newRow;
        });

        // console.log('formattedData:: ', formattedData);

        section.data.push({
          type: 'table',
          content: {
            columns: columns,
            data: formattedData
          }
        });
      }
    };

    const parseAndAddContent = (node) => {
      if (!node) return null;

      if (node.type !== 'text' && node.name !== 'table') {
        const nodeContents = $(node).contents().toArray();
        nodeContents.forEach((_node) => parseAndAddContent(_node));
      } else if (node.type === 'text' || node.name === 'br') {
        const content = $(node)
          .find('br')
          .replaceWith('\n')
          .end()
          .text()
          .trim();

        const paragraphs = content.split(/\n{2,}/).filter((p) => p.trim());

        // Add each paragraph as a separate entry
        paragraphs.forEach((paragraph) => {
          section.data.push({
            type: 'paragraph',
            content: paragraph.trim()
          });
        });
      } else if (node.name === 'table') {
        parseTable(node);
      }
    };

    sectionContents.forEach((node) => parseAndAddContent(node));

    if (section.title) {
      body.push(section);
    }
  });

  const attachments = [];

  const searchData = createSearchData(body);

  return { header: metaData, body, searchData, attachments };
};

export default parseDocument;
